import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search, ChevronLeft, ChevronRight, Monitor, Smartphone } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { MenuRenderer } from "@/components/app/MenuRenderer";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };

const SIDEBAR_FILTERS = [
  { key: "home",       label: "Home",       isHome: true },
  { key: "yours",      label: "Your Designs", route: "/dashboard/designs" },
  { type: "section", label: "Categories" },
  { key: "all",        label: "All" },
  { key: "Featured",   label: "Featured" },
  { key: "Popular",    label: "Popular" },
  { key: "Promo & Sale", label: "Promo & Sale" },
  { key: "4th of July", label: "4th of July" },
  { key: "Holidays & Observances", label: "Holidays" },
  { key: "Menu",       label: "Menu" },
];

export default function Templates() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("home");
  const [orientation, setOrientation] = useState(""); // "" | "landscape" | "portrait"
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  // Load home rows
  useEffect(() => {
    api.get("/templates/by-category").then((r) => setRows(r.data.rows || []));
  }, []);

  // Run search whenever query or orientation changes
  useEffect(() => {
    if (!search.trim() && !orientation) { setSearchResults(null); return; }
    const t = setTimeout(() => {
      const params = { limit: 60 };
      if (search.trim()) params.q = search;
      if (orientation) params.orientation = orientation;
      api.get("/templates", { params }).then((r) => setSearchResults(r.data.templates || []));
    }, 300);
    return () => clearTimeout(t);
  }, [search, orientation]);

  const applyTemplate = async (t) => {
    try {
      const r = await api.post("/designs", {
        name: `${t.name} – ${new Date().toLocaleDateString()}`,
        template_id: t.id,
        config: t.default_config,
      });
      toast.success("Design created");
      navigate(`/dashboard/designs/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <DashboardLayout>
      <div className="flex min-h-screen">
        {/* Templates side rail (hidden on mobile — replaced by horizontal
            scrolling filter pills inside the main content header below). */}
        <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r bg-white p-4 sticky top-0 self-start" style={{ borderColor: "var(--border-light)" }} data-testid="templates-sidebar">
          <NewDesignButton onPick={(o) => createBlankDesign(o, navigate)} />
          <Button
            onClick={() => navigate("/dashboard/designs")}
            variant="outline"
            className="w-full rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 mb-4"
            data-testid="your-designs-btn"
          >
            Your Designs
          </Button>

          <div className="space-y-0.5">
            {SIDEBAR_FILTERS.map((it, idx) => {
              if (it.type === "section") {
                return (
                  <div key={`sec-${idx}`} className="pt-4 pb-1 px-2 text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-400">
                    {it.label}
                  </div>
                );
              }
              const active = filter === it.key;
              return (
                <button
                  key={it.key}
                  onClick={() => {
                    if (it.route) return navigate(it.route);
                    setFilter(it.key);
                  }}
                  data-testid={`sidebar-filter-${it.key}`}
                  className={`w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors ${
                    active ? "bg-sky-50 text-sky-700 font-medium" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {it.label}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Top search header */}
          <div className="sticky top-0 z-10 bg-white/85 backdrop-blur-md border-b" style={{ borderColor: "var(--border-light)" }}>
            <div className="px-4 sm:px-6 md:px-10 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 shrink-0" style={headingFont}>Templates</h1>
              <div className="flex-1 max-w-2xl relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search templates: pizza, gym, sale…"
                  className="pl-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-full h-11"
                  data-testid="templates-search-input"
                />
              </div>
              {/* Orientation switcher */}
              <div className="flex gap-1.5 bg-slate-100 rounded-full p-1 self-start sm:self-auto" data-testid="orientation-switcher">
                {[
                  { key: "", label: "All" },
                  { key: "landscape", label: "16:9" },
                  { key: "portrait", label: "9:16" },
                ].map((o) => (
                  <button
                    key={o.key || "all"}
                    onClick={() => setOrientation(o.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      orientation === o.key
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                    data-testid={`orientation-${o.key || "all"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile-only horizontal pills (replaces the hidden side rail).
                Lets users filter by category without losing screen space to a
                permanent left nav. */}
            <div className="lg:hidden border-t" style={{ borderColor: "var(--border-light)" }}>
              <div className="px-4 py-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="templates-mobile-filters">
                {SIDEBAR_FILTERS
                  .filter((it) => it.type !== "section" && !it.route)
                  .map((it) => {
                    const active = filter === it.key;
                    return (
                      <button
                        key={`m-${it.key}`}
                        onClick={() => setFilter(it.key)}
                        data-testid={`m-filter-${it.key}`}
                        className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          active
                            ? "bg-sky-500 text-white border-sky-500"
                            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {it.label}
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-8">
            {/* SEARCH / ORIENTATION FILTER RESULTS */}
            {(search.trim() || orientation) && (
              <Section
                title={search.trim() ? `Search: "${search}"` : (orientation === "portrait" ? "Portrait · 9:16" : orientation === "landscape" ? "Landscape · 16:9" : "All")}
                hint={`${(searchResults || []).length} matches`}
              >
                <Grid templates={searchResults || []} applyTemplate={applyTemplate} />
              </Section>
            )}

            {/* CATEGORY FILTER */}
            {!search.trim() && !orientation && filter !== "home" && filter !== "all" && (
              <CategoryGrid category={filter} applyTemplate={applyTemplate} />
            )}

            {/* ALL */}
            {!search.trim() && !orientation && filter === "all" && <AllGrid applyTemplate={applyTemplate} />}

            {/* HOME = horizontal category rows */}
            {!search.trim() && !orientation && filter === "home" && (
              <div className="space-y-12">
                {rows.map((row) => (
                  <CategoryRow key={row.id} row={row} applyTemplate={applyTemplate} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

/**
 * Reusable "+ New Design" button that pops a mini orientation picker and
 * creates a blank canvas of the chosen aspect ratio. Owns no data itself —
 * delegates the actual API call to `onPick(orientation)`.
 *
 * Used by both Templates.jsx sidebar and Designs.jsx header.
 */
export function NewDesignButton({ onPick, variant = "primary", label = "+ New Design" }) {
  const [open, setOpen] = useState(false);
  const trigger = (
    <Button
      className={variant === "primary"
        ? "w-full rounded-lg bg-sky-500 hover:bg-sky-600 text-white shadow-sm mb-2"
        : "rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm"
      }
      data-testid="new-design-btn"
    >
      {label}
    </Button>
  );
  const pick = (o) => {
    setOpen(false);
    onPick(o);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-80 p-3 bg-white border border-slate-200" align="start">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
          Start with a blank canvas
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => pick("landscape")}
            className="group flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-sky-400 hover:bg-sky-50 transition-all"
            data-testid="new-design-blank-landscape"
          >
            <div className="w-16 h-9 rounded-md border-2 border-slate-300 group-hover:border-sky-400 bg-slate-50 flex items-center justify-center">
              <Monitor className="h-4 w-4 text-slate-400 group-hover:text-sky-500" />
            </div>
            <div className="text-xs font-semibold text-slate-800">Landscape</div>
            <div className="text-[10px] text-slate-500">16:9 · 1920×1080</div>
          </button>
          <button
            type="button"
            onClick={() => pick("portrait")}
            className="group flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-sky-400 hover:bg-sky-50 transition-all"
            data-testid="new-design-blank-portrait"
          >
            <div className="w-9 h-16 rounded-md border-2 border-slate-300 group-hover:border-sky-400 bg-slate-50 flex items-center justify-center">
              <Smartphone className="h-4 w-4 text-slate-400 group-hover:text-sky-500" />
            </div>
            <div className="text-xs font-semibold text-slate-800">Portrait</div>
            <div className="text-[10px] text-slate-500">9:16 · 1080×1920</div>
          </button>
        </div>
        <div className="text-[10px] text-slate-400 text-center mt-3">
          Blank canvas · you can change the size later in the editor
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Shared helper — creates a blank design of the chosen orientation and
 * navigates into the editor. Exported so Designs.jsx can share the exact
 * same behaviour without duplicating the API payload shape.
 */
export async function createBlankDesign(orientation, navigate) {
  const w = orientation === "portrait" ? 1080 : 1920;
  const h = orientation === "portrait" ? 1920 : 1080;
  try {
    const r = await api.post("/designs", {
      name: `Untitled – ${new Date().toLocaleDateString()}`,
      template_id: "blank",
      config: {
        elements: [],
        background_color: "#FFFFFF",
        layout: "freeform",
        canvas_w: w,
        canvas_h: h,
      },
    });
    toast.success("Blank canvas ready");
    navigate(`/dashboard/designs/${r.data.id}`);
  } catch (err) {
    toast.error(formatApiError(err));
  }
}



function Section({ title, hint, children }) {
  return (
    <div className="mb-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-xl font-bold text-slate-900" style={headingFont}>{title}</h2>
        {hint && <div className="text-sm text-slate-500">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function CategoryRow({ row, applyTemplate }) {
  const scroller = useRef(null);
  const scroll = (dx) => scroller.current?.scrollBy({ left: dx, behavior: "smooth" });
  return (
    <div data-testid={`row-${row.id}`}>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xl font-bold text-slate-900" style={headingFont}>{row.label}</h2>
        <div className="flex items-center gap-1">
          <button onClick={() => scroll(-600)} className="h-8 w-8 rounded-full bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500" aria-label="Scroll left">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => scroll(600)} className="h-8 w-8 rounded-full bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500" aria-label="Scroll right">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div ref={scroller} className="flex gap-4 overflow-x-auto pb-3 scroll-row items-start" style={{ scrollSnapType: "x mandatory" }}>
        {(row.templates || []).map((t) => (
          <TemplateCard key={t.id} template={t} applyTemplate={applyTemplate} compact />
        ))}
      </div>
    </div>
  );
}

function Grid({ templates, applyTemplate }) {
  if (!templates.length) {
    return <div className="text-slate-500 text-sm py-12 text-center">No matches.</div>;
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 items-start" data-testid="templates-grid">
      {templates.map((t) => <TemplateCard key={t.id} template={t} applyTemplate={applyTemplate} />)}
    </div>
  );
}

function CategoryGrid({ category, applyTemplate }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (category === "Featured") {
      api.get("/templates/featured").then((r) => setItems(r.data.templates || []));
    } else {
      api.get("/templates", { params: { category, limit: 200 } }).then((r) => setItems(r.data.templates || []));
    }
  }, [category]);
  return <Section title={category} hint={`${items.length} templates`}><Grid templates={items} applyTemplate={applyTemplate} /></Section>;
}

function AllGrid({ applyTemplate }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/templates", { params: { limit: 300 } }).then((r) => setItems(r.data.templates || []));
  }, []);
  return <Section title="All templates" hint={`${items.length} templates`}><Grid templates={items} applyTemplate={applyTemplate} /></Section>;
}

function TemplateCard({ template, applyTemplate, compact }) {
  const cw = template.default_config?.canvas_w || 1920;
  const ch = template.default_config?.canvas_h || 1080;
  const isPortrait = ch > cw;
  const ratio = `${cw} / ${ch}`;
  // Rail rows use a fixed card width; grid cells stretch to their column, so
  // measure the container width at runtime and compute the miniature scale
  // from that instead of hard-coding cardWidthPx. This kills the dead space
  // that used to appear when a portrait card was wider than the assumed 220px.
  const w = compact ? (isPortrait ? "min-w-[180px] w-[180px]" : "min-w-[280px] w-[280px]") : "";
  const previewRef = useRef(null);
  const [scale, setScale] = useState(0.2);
  useLayoutEffect(() => {
    if (!previewRef.current) return;
    const el = previewRef.current;
    const update = () => {
      const containerWidth = el.clientWidth;
      if (containerWidth > 0) setScale(containerWidth / cw);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cw]);
  return (
    <div
      className={`group rounded-xl border border-slate-200 bg-white overflow-hidden hover:-translate-y-0.5 hover:shadow-lg hover:border-sky-300 transition-all cursor-pointer ${w}`}
      style={{ scrollSnapAlign: "start" }}
      onClick={() => applyTemplate(template)}
      data-testid={`template-${template.id}`}
    >
      <div ref={previewRef} className="relative overflow-hidden bg-slate-100" style={{ aspectRatio: ratio }}>
        {/* Live miniature preview */}
        {template.default_config && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              transformOrigin: "0 0",
              transform: `scale(${scale})`,
              width: `${cw}px`,
              height: `${ch}px`,
            }}
          >
            <MenuRenderer config={template.default_config} scale="tv" />
          </div>
        )}
        {/* Orientation badge */}
        <div
          className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${
            isPortrait ? "bg-purple-500 text-white" : "bg-sky-500 text-white"
          }`}
          data-testid={`template-orientation-${isPortrait ? "portrait" : "landscape"}`}
        >
          {isPortrait ? "9:16" : "16:9"}
        </div>
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
          <Button size="sm" className="rounded-full bg-white hover:bg-slate-100 text-slate-900 text-xs h-8" data-testid={`use-template-${template.id}`}>
            Use this template <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
      <div className="p-3">
        <div className="text-sm font-semibold text-slate-900 truncate" style={headingFont}>{template.name}</div>
        {template.category && (
          <div className="text-[11px] text-slate-500 mt-0.5">{template.category}</div>
        )}
      </div>
    </div>
  );
}
